import React, { useEffect, useState } from 'react';
import type { BioData, CityLink, OnboardingStage, Qualification } from '../types/onboarding';
import { fetchCityLinks, findCityById, scoreApplication, submitOnboarding } from '../utils/tnsOnboarding';
import { OnboardingShell } from '../components/Onboarding/OnboardingShell';
import { StepBioData } from '../components/Onboarding/StepBioData';
import { StepQualification } from '../components/Onboarding/StepQualification';
import { ReviewingState } from '../components/Onboarding/ReviewingState';
import { OutcomeDeclined, OutcomeUnderReview, OutcomeWelcome } from '../components/Onboarding/OutcomeScreens';
const emptyBio: BioData = {
  fullName: '',
  email: '',
  phone: '',
  brandName: '',
  expertise: '',
  cityId: '',
  otherCityName: ''
};
const emptyQualification: Qualification = {
  q1HelpedSomeone: '',
  q2ShapedCulture: '',
  q3BuildingNow: '',
  q4BringToRoom: '',
  consistencyRating: null,
  financialCommitment: ''
};

/** Minimum time the reviewing state stays up, so it never flashes. */
const MIN_REVIEW_MS = 1700;
export const JoinTNS = () => {
  const [stage, setStage] = useState<OnboardingStage>('bio');
  const [bio, setBio] = useState<BioData>(emptyBio);
  const [qualification, setQualification] = useState<Qualification>(emptyQualification);
  const [cities, setCities] = useState<CityLink[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Read the City Links tab on load.
  useEffect(() => {
    let cancelled = false;
    fetchCityLinks().then(rows => {
      if (!cancelled) setCities(rows);
    }).finally(() => {
      if (!cancelled) setCitiesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Every stage change starts at the top of the screen.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [stage]);
  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    setStage('reviewing');
    const startedAt = Date.now();
    try {
      // 1. Score first (hard filter short-circuits the AI call).
      const {
        aiScore,
        decision
      } = await scoreApplication(qualification);

      // 2. Then the single write — Step 1 and Step 2 combined, one row.
      await submitOnboarding({
        bio,
        qualification,
        cities,
        aiScore,
        decision
      });

      // 3. Hold the reviewing state long enough to read as consideration.
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_REVIEW_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_REVIEW_MS - elapsed));
      }
      setStage(decision);
    } catch {
      setSubmitError('We couldn’t send your application just then. Please try once more.');
      setStage('qualification');
    } finally {
      setSubmitting(false);
    }
  };
  if (stage === 'reviewing') return <ReviewingState data-id="element-3637" data-mp="117n7ydbov1kw" />;
  if (stage === 'approved') {
    const city = findCityById(cities, bio.cityId);
    const cityName = bio.cityId === 'OTH' && bio.otherCityName.trim() ? bio.otherCityName.trim() : city?.city_name ?? '';
    return <OutcomeWelcome cityName={cityName} whatsappLink={city?.whatsapp_link ?? ''} data-id="element-3638" data-mp="168bb1xd6z8vh" />;
  }
  if (stage === 'review') return <OutcomeUnderReview data-id="element-3639" data-mp="igw2vnyqhxcb" />;
  if (stage === 'declined') return <OutcomeDeclined data-id="element-3640" data-mp="dkoxpxe5wpvf" />;
  return <OnboardingShell data-id="element-3641" data-mp="dlqhcq98ukor">
      {submitError && <div role="alert" className="mb-8 rounded-lg border border-[#C98B7F] bg-[#FBF1EE] px-5 py-4 font-body text-sm text-[#9B3B2F]" data-id="element-3642" data-mp="s9bmeppoitzb">
          {submitError}
        </div>}

      {stage === 'bio' ? <StepBioData value={bio} onChange={setBio} cities={cities} citiesLoading={citiesLoading} onContinue={() => setStage('qualification')} data-id="element-3643" data-mp="3vwbykhsgez4t" /> : <StepQualification value={qualification} onChange={setQualification} onBack={() => setStage('bio')} onSubmit={handleFinalSubmit} submitting={submitting} data-id="element-3644" data-mp="2u99j4i5duxcs" />}
    </OnboardingShell>;
};